import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { 
  Power, MapPin, Clock, DollarSign, CheckCircle, XCircle,
  Navigation, AlertCircle, Bell, Settings, ChevronRight,
  Briefcase, Star, TrendingUp, MessageCircle, LogOut, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const ContractorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  
  // State
  const [profile, setProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  
  // Incoming offer state
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [offerCountdown, setOfferCountdown] = useState(0);
  const [respondingToOffer, setRespondingToOffer] = useState(false);
  
  // Logout state
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutWarning, setLogoutWarning] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
    fetchActiveJob();
    
    // Listen for logout events to clean up WebSocket
    const handleLogoutEvent = () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    
    window.addEventListener('auth:logout', handleLogoutEvent);
    return () => window.removeEventListener('auth:logout', handleLogoutEvent);
  }, []);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !isOnline) return;

    const connectWebSocket = () => {
      const wsUrl = `${WS_URL}/ws/${token}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[WS] Connected');
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('[WS] Message:', data);

        if (data.event === 'job_offer') {
          handleIncomingOffer(data.data);
        } else if (data.event === 'offer_canceled') {
          if (incomingOffer?.job_id === data.data.job_id) {
            setIncomingOffer(null);
            toast.info('Offer was cancelled: ' + data.data.reason);
          }
        }
      };

      wsRef.current.onclose = () => {
        console.log('[WS] Disconnected');
        // Reconnect after 3 seconds if still online
        if (isOnline) {
          setTimeout(connectWebSocket, 3000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
    };

    connectWebSocket();

    // Send location updates every 15 seconds (Uber-style location tracking)
    const locationInterval = setInterval(async () => {
      if (!isOnline) return;
      
      // Get fresh location if available
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const newLoc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setCurrentLocation(newLoc);
            
            // Send via REST API for reliable location updates
            try {
              await axios.post(`${API_URL}/contractors/location`, {
                latitude: newLoc.lat,
                longitude: newLoc.lng,
                accuracy_meters: position.coords.accuracy,
                heading: position.coords.heading,
                speed_mps: position.coords.speed
              });
            } catch (err) {
              console.error('Failed to update location:', err);
            }
            
            // Also send via WebSocket for real-time updates
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'location_update',
                latitude: newLoc.lat,
                longitude: newLoc.lng
              }));
            }
          },
          (error) => {
            console.warn('Location update failed:', error);
            // Use last known location
            if (currentLocation && wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'location_update',
                latitude: currentLocation.lat,
                longitude: currentLocation.lng
              }));
            }
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      }
    }, 15000);

    return () => {
      clearInterval(locationInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isOnline]);  // Remove currentLocation dependency to avoid loop

  // Countdown timer for offers
  useEffect(() => {
    if (!incomingOffer) return;

    const expiresAt = new Date(incomingOffer.expires_at).getTime();
    
    const timer = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setOfferCountdown(remaining);

      if (remaining === 0) {
        setIncomingOffer(null);
        toast.error('Offer expired');
      }
    }, 100);

    return () => clearInterval(timer);
  }, [incomingOffer]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/contractors/profile`);
      setProfile(response.data);
      setIsOnline(response.data.is_online);
      setRadiusMiles(response.data.service_radius_miles || 10);
      if (response.data.current_latitude && response.data.current_longitude) {
        setCurrentLocation({
          lat: response.data.current_latitude,
          lng: response.data.current_longitude
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/contractor/onboarding');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveJob = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs?status=accepted`);
      const activeJobs = response.data.filter(j => 
        ['accepted', 'assigned', 'en_route', 'arrived', 'in_progress'].includes(j.status)
      );
      if (activeJobs.length > 0) {
        setActiveJob(activeJobs[0]);
      }
    } catch (error) {
      console.error('Failed to fetch active job');
    }
  };

  const fetchAvailableJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs/available`);
      setAvailableJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch available jobs');
    }
  };

  const handleIncomingOffer = (offerData) => {
    // Play notification sound
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleUo4ZsXszK6jfHNlWFFXe5+4u5N2V0hMVnKOo7O0npB/cWtpbnuJmaKgk5CHhIaJi5qmoZ2bmZiYmJqcn5+enJuamJiYmZqcnZ2cm5uamZiYmZqbm5ubm5qampqampubm5ubm5ubm5ub');
      audio.play();
    } catch (e) {
      // Audio play may fail silently on some browsers
    }

    setIncomingOffer(offerData);
    setOfferCountdown(offerData.ttl_seconds);
  };

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      setShowLocationModal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        setLocationError(null);
        toast.success('Location updated');
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationError('Location access denied');
        setShowLocationModal(true);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // State for location permission modal
  const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  
  const toggleOnlineStatus = async () => {
    if (!isOnline) {
      // MANDATORY: Need location to go online (Uber-style requirement)
      if (!currentLocation) {
        // Show the location permission modal first
        setShowLocationPermissionModal(true);
        return;
      }
      
      // Have location - proceed to go online
      await goOnline();
    } else {
      // Going offline - no location needed
      await goOffline();
    }
  };
  
  const goOnline = async () => {
    if (!currentLocation) {
      toast.error('Live location is required to receive jobs');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/contractors/go-online`, {
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
        source: 'gps'
      });
      
      setIsOnline(true);
      toast.success('You are now online and can receive job offers!');
      fetchAvailableJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to go online');
    }
  };
  
  const goOffline = async () => {
    try {
      await axios.post(`${API_URL}/contractors/go-offline`);
      setIsOnline(false);
      toast.info('You are now offline');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to go offline');
    }
  };
  
  const handleRequestLocation = async () => {
    setRequestingLocation(true);
    setLocationDenied(false);
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
      
      const loc = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      setCurrentLocation(loc);
      setShowLocationPermissionModal(false);
      setLocationError(null);
      
      // Auto-proceed to go online
      await axios.post(`${API_URL}/contractors/go-online`, {
        latitude: loc.lat,
        longitude: loc.lng,
        source: 'gps'
      });
      
      setIsOnline(true);
      toast.success('You are now online and can receive job offers!');
      fetchAvailableJobs();
      
    } catch (error) {
      if (error.code === 1) {
        // Permission denied
        setLocationDenied(true);
        setLocationError('Location permission denied. You cannot receive jobs without sharing your location.');
        toast.error('Location access is required to receive jobs');
      } else {
        setLocationError('Unable to get your location. Please try again or set manually.');
        toast.error('Failed to get location');
      }
    } finally {
      setRequestingLocation(false);
    }
  };
  
  const handleManualLocationFallback = () => {
    setShowLocationPermissionModal(false);
    setShowLocationModal(true);
  };

  const updateRadius = async (value) => {
    setRadiusMiles(value[0]);
    try {
      await axios.post(`${API_URL}/contractors/radius`, {
        radius_miles: value[0]
      });
    } catch (error) {
      toast.error('Failed to update radius');
    }
  };

  const setManualLocation = async (lat, lng) => {
    setCurrentLocation({ lat, lng });
    setShowLocationModal(false);
    
    try {
      await axios.post(`${API_URL}/contractors/location`, {
        latitude: lat,
        longitude: lng
      });
      toast.success('Location set successfully');
    } catch (error) {
      toast.error('Failed to update location');
    }
  };

  const respondToOffer = async (action) => {
    if (!incomingOffer || respondingToOffer) return;
    
    setRespondingToOffer(true);
    
    try {
      await axios.post(`${API_URL}/offers/${incomingOffer.offer_id}/respond`, {
        action: action
      });
      
      if (action === 'accept') {
        toast.success('Job accepted! Navigate to the customer.');
        fetchActiveJob();
      } else {
        toast.info('Job declined');
      }
      
      setIncomingOffer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setRespondingToOffer(false);
    }
  };

  const updateJobStatus = async (newStatus) => {
    if (!activeJob) return;
    
    try {
      await axios.patch(`${API_URL}/jobs/${activeJob.id}/status`, null, {
        params: { new_status: newStatus }
      });
      
      if (newStatus === 'completed') {
        setActiveJob(null);
        toast.success('Job completed!');
      } else {
        setActiveJob(prev => ({ ...prev, status: newStatus }));
        toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || currency;
  };

  const handleLogout = async (force = false) => {
    setLoggingOut(true);
    
    try {
      // Close WebSocket before logout
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const result = await logout({ force });
      
      if (!result.success && result.warning) {
        // Show warning dialog for active job
        setLogoutWarning(result);
        setShowLogoutDialog(true);
        setLoggingOut(false);
        return;
      }
      
      // Successful logout - redirect to landing page
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
      setLoggingOut(false);
    }
  };

  const confirmForceLogout = async () => {
    setShowLogoutDialog(false);
    setLoggingOut(true);
    
    try {
      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      await logout({ force: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to log out');
      setLoggingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Incoming Job Offer Modal */}
      <AnimatePresence>
        {incomingOffer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              className="bg-neutral-900 rounded-3xl w-full max-w-md overflow-hidden"
            >
              {/* Countdown Timer */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Respond within</span>
                  <div className="flex items-center gap-2">
                    <div className="bg-white/20 rounded-full px-4 py-2">
                      <span className="text-2xl font-bold text-white font-mono">
                        {offerCountdown}s
                      </span>
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-white"
                    initial={{ width: '100%' }}
                    animate={{ width: `${(offerCountdown / incomingOffer.ttl_seconds) * 100}%` }}
                  />
                </div>
              </div>

              {/* Job Details */}
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-2">{incomingOffer.trade_name || incomingOffer.category}</h2>
                <p className="text-neutral-400 mb-6">{incomingOffer.sub_trade_name || incomingOffer.subcategory}</p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-neutral-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-neutral-400 mb-1">
                      <Navigation className="w-4 h-4" />
                      <span className="text-sm">Distance</span>
                    </div>
                    <p className="text-xl font-bold">{incomingOffer.distance_miles.toFixed(1)} mi</p>
                  </div>
                  <div className="bg-neutral-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-neutral-400 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">ETA</span>
                    </div>
                    <p className="text-xl font-bold">{incomingOffer.eta_minutes} min</p>
                  </div>
                </div>

                {/* Contractor Earnings Breakdown - MANDATORY TRANSPARENCY */}
                <div className="bg-neutral-800 rounded-xl p-4 mb-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">Job total (customer pays)</span>
                    <span className="text-white font-medium">
                      {getCurrencySymbol(incomingOffer.currency)}{incomingOffer.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">ZOLT platform fee ({incomingOffer.platform_fee_percent || 6}%)</span>
                    <span className="text-red-400">
                      -{getCurrencySymbol(incomingOffer.currency)}
                      {(incomingOffer.platform_fee_amount || (incomingOffer.price * 0.06)).toFixed(2)}
                    </span>
                  </div>
                  <hr className="border-neutral-700" />
                  <div className="flex justify-between">
                    <span className="text-white font-semibold">Your payout</span>
                    <span className="text-2xl font-bold text-green-400">
                      {getCurrencySymbol(incomingOffer.currency)}
                      {(incomingOffer.contractor_payout || (incomingOffer.price * 0.94)).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="bg-neutral-800 rounded-xl p-4 mb-6">
                  <p className="text-neutral-400 text-sm mb-2">Area</p>
                  <p className="text-white">{incomingOffer.address_area}</p>
                </div>

                <p className="text-neutral-400 text-sm mb-6 line-clamp-2">
                  {incomingOffer.description}
                </p>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={() => respondToOffer('decline')}
                    disabled={respondingToOffer}
                    className="bg-neutral-800 hover:bg-neutral-700 text-white h-14 rounded-xl"
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={() => respondToOffer('accept')}
                    disabled={respondingToOffer}
                    className="bg-green-600 hover:bg-green-500 text-white h-14 rounded-xl"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Accept
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Permission Modal (Uber-style requirement) */}
      <AnimatePresence>
        {showLocationPermissionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            data-testid="location-permission-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-neutral-900 rounded-2xl p-6 w-full max-w-md border border-neutral-700"
            >
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <MapPin className="w-10 h-10 text-blue-400" />
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-3 text-center">
                Live Location Required
              </h3>
              
              <p className="text-neutral-400 mb-6 text-center">
                <span className="text-white font-medium">ZOLT requires live location access</span> to match you with nearby jobs. 
                This is how we connect you with customers in your area.
              </p>
              
              {locationDenied && (
                <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-300 text-sm font-medium">Location Access Denied</p>
                      <p className="text-red-400 text-xs mt-1">
                        Without location sharing, you cannot receive job offers. 
                        Please enable location in your browser settings or set a manual location.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <Button
                  onClick={handleRequestLocation}
                  disabled={requestingLocation}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium"
                  data-testid="enable-location-btn"
                >
                  {requestingLocation ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <Navigation className="w-5 h-5 mr-2" />
                      Enable Live Location
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleManualLocationFallback}
                  variant="outline"
                  className="w-full h-12 bg-neutral-800 hover:bg-neutral-700 text-white border-neutral-700 rounded-xl"
                  data-testid="manual-location-btn"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Set Location Manually
                </Button>
                
                <Button
                  onClick={() => setShowLocationPermissionModal(false)}
                  variant="ghost"
                  className="w-full h-10 text-neutral-400 hover:text-white"
                  data-testid="cancel-location-btn"
                >
                  Cancel
                </Button>
              </div>
              
              <p className="text-neutral-500 text-xs text-center mt-6">
                Your location is only shared while you&apos;re online and only visible to customers after you accept their job.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Modal */}
      <AnimatePresence>
        {showLocationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-neutral-900 rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold mb-4">Set Your Location</h3>
              <p className="text-neutral-400 mb-6">
                {locationError || 'Please set your location to receive job offers nearby.'}
              </p>
              
              <Button
                onClick={requestLocation}
                className="w-full bg-blue-600 hover:bg-blue-500 mb-4"
              >
                <Navigation className="w-5 h-5 mr-2" />
                Use My Current Location
              </Button>
              
              <div className="text-center text-neutral-500 mb-4">or</div>
              
              {/* Manual location - London preset for testing */}
              <Button
                onClick={() => setManualLocation(51.5074, -0.1278)}
                variant="outline"
                className="w-full border-neutral-700 mb-2"
              >
                Set to Central London
              </Button>
              <Button
                onClick={() => setManualLocation(51.4545, -2.5879)}
                variant="outline"
                className="w-full border-neutral-700 mb-4"
              >
                Set to Bristol
              </Button>
              
              <Button
                onClick={() => setShowLocationModal(false)}
                variant="ghost"
                className="w-full"
              >
                Cancel
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="sticky top-0 bg-black/95 backdrop-blur-lg z-30 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">ZOLT</h1>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/notifications')}
              className="p-2 hover:bg-neutral-800 rounded-full relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-6 h-6" />
            </button>
            <button 
              onClick={() => navigate('/settings')}
              className="p-2 hover:bg-neutral-800 rounded-full"
              data-testid="settings-btn"
            >
              <Settings className="w-6 h-6" />
            </button>
            
            {/* Profile/Account Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-2 p-2 hover:bg-neutral-800 rounded-full"
                  data-testid="profile-menu-trigger"
                >
                  <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-neutral-300" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-neutral-900 border-neutral-800">
                <DropdownMenuLabel className="text-white">
                  {user?.name || 'Contractor'}
                  <p className="text-xs text-neutral-400 font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuItem 
                  onClick={() => navigate('/settings')}
                  className="text-neutral-200 hover:bg-neutral-800 cursor-pointer"
                  data-testid="menu-settings"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/messages')}
                  className="text-neutral-200 hover:bg-neutral-800 cursor-pointer"
                  data-testid="menu-messages"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Messages
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuItem 
                  onClick={() => handleLogout()}
                  disabled={loggingOut}
                  className="text-red-400 hover:bg-red-900/30 hover:text-red-300 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {loggingOut ? 'Logging out...' : 'Log out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Online Toggle - Hero Section */}
        <div className={`rounded-3xl p-8 mb-8 transition-all duration-500 ${
          isOnline 
            ? 'bg-gradient-to-br from-green-900/50 to-green-800/30 border border-green-500/30' 
            : 'bg-neutral-900 border border-neutral-800'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">
                {isOnline ? "You're Online" : "You're Offline"}
              </h2>
              <p className="text-neutral-400">
                {isOnline 
                  ? 'Accepting live job offers in your area' 
                  : 'Toggle on to start receiving jobs'}
              </p>
            </div>
            <button
              onClick={toggleOnlineStatus}
              className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                isOnline 
                  ? 'bg-green-500 shadow-lg shadow-green-500/50' 
                  : 'bg-neutral-700 hover:bg-neutral-600'
              }`}
            >
              <Power className={`w-10 h-10 ${isOnline ? 'text-white' : 'text-neutral-400'}`} />
            </button>
          </div>

          {/* Location & Radius */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  <span className="font-medium">Your Location</span>
                </div>
                <Button
                  onClick={() => setShowLocationModal(true)}
                  size="sm"
                  variant="outline"
                  className="border-neutral-700 text-sm"
                >
                  Update
                </Button>
              </div>
              {currentLocation ? (
                <p className="text-neutral-400 text-sm">
                  {currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)}
                </p>
              ) : (
                <p className="text-red-400 text-sm">Location not set</p>
              )}
            </div>

            <div className="bg-black/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-purple-400" />
                  <span className="font-medium">Service Radius</span>
                </div>
                <span className="text-lg font-bold">{radiusMiles} miles</span>
              </div>
              <Slider
                value={[radiusMiles]}
                onValueChange={updateRadius}
                min={1}
                max={30}
                step={1}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-neutral-500 mt-1">
                <span>1 mi</span>
                <span>30 mi (max)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Job Card */}
        {activeJob && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Active Job</h3>
              <span className="px-3 py-1 bg-blue-500 rounded-full text-sm font-medium">
                {activeJob.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            
            <div className="mb-4">
              <p className="text-lg font-semibold">{activeJob.category} - {activeJob.subcategory}</p>
              <p className="text-neutral-400 text-sm mt-1">{activeJob.description}</p>
            </div>

            {/* Earnings Breakdown - Contractor Transparency */}
            <div className="bg-black/40 rounded-xl p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-400">Job total (customer pays)</span>
                <span className="text-white">{getCurrencySymbol(activeJob.currency)}{activeJob.estimated_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-400">ZOLT platform fee (6%)</span>
                <span className="text-red-400">-{getCurrencySymbol(activeJob.currency)}{(activeJob.estimated_price * 0.06).toFixed(2)}</span>
              </div>
              <hr className="border-neutral-700" />
              <div className="flex justify-between items-center">
                <span className="text-white font-medium">Your payout</span>
                <span className="text-2xl font-bold text-green-400">
                  {getCurrencySymbol(activeJob.currency)}{(activeJob.estimated_price * 0.94).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Status Update Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {activeJob.status === 'accepted' && (
                <Button
                  onClick={() => updateJobStatus('en_route')}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Start En Route
                </Button>
              )}
              {activeJob.status === 'en_route' && (
                <Button
                  onClick={() => updateJobStatus('arrived')}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Mark Arrived
                </Button>
              )}
              {activeJob.status === 'arrived' && (
                <Button
                  onClick={() => updateJobStatus('in_progress')}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Start Work
                </Button>
              )}
              {activeJob.status === 'in_progress' && (
                <Button
                  onClick={() => updateJobStatus('completed')}
                  className="bg-green-600 hover:bg-green-500"
                >
                  Complete Job
                </Button>
              )}
              <Button
                onClick={() => navigate(`/jobs/${activeJob.id}/messages`)}
                variant="outline"
                className="border-neutral-700"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Message Customer
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {profile && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-neutral-900 rounded-xl p-4 border border-neutral-800">
              <div className="flex items-center gap-2 text-neutral-400 mb-2">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm">Jobs Completed</span>
              </div>
              <p className="text-2xl font-bold">{profile.total_jobs_completed}</p>
            </div>
            <div className="bg-neutral-900 rounded-xl p-4 border border-neutral-800">
              <div className="flex items-center gap-2 text-neutral-400 mb-2">
                <Star className="w-4 h-4" />
                <span className="text-sm">Rating</span>
              </div>
              <p className="text-2xl font-bold">{profile.average_rating.toFixed(1)}</p>
            </div>
            <div className="bg-neutral-900 rounded-xl p-4 border border-neutral-800">
              <div className="flex items-center gap-2 text-neutral-400 mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Acceptance</span>
              </div>
              <p className="text-2xl font-bold">{profile.acceptance_rate}%</p>
            </div>
            <div className="bg-neutral-900 rounded-xl p-4 border border-neutral-800">
              <div className="flex items-center gap-2 text-neutral-400 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Status</span>
              </div>
              <p className="text-2xl font-bold text-green-400">
                {profile.is_verified ? 'Verified' : 'Pending'}
              </p>
            </div>
          </div>
        )}

        {/* Skills */}
        {profile?.skills && (
          <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800 mb-8">
            <h3 className="text-lg font-bold mb-4">Your Skills</h3>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-neutral-800 rounded-full text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Offline Message */}
        {!isOnline && (
          <div className="bg-neutral-900 rounded-2xl p-8 text-center border border-neutral-800">
            <Power className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
            <h3 className="text-xl font-bold mb-2">You&apos;re Currently Offline</h3>
            <p className="text-neutral-400 mb-6">
              Toggle on &quot;Accept live jobs&quot; to start receiving job offers
            </p>
            <Button
              onClick={toggleOnlineStatus}
              className="bg-green-600 hover:bg-green-500"
              data-testid="go-online-btn"
            >
              Go Online
            </Button>
          </div>
        )}
      </div>

      {/* Logout Warning Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="bg-neutral-900 border-neutral-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Active Job Warning
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              {logoutWarning?.warning}
              {logoutWarning?.activeJobStatus && (
                <span className="block mt-2 text-yellow-400">
                  Current status: {logoutWarning.activeJobStatus.replace('_', ' ').toUpperCase()}
                </span>
              )}
              <span className="block mt-2">
                If you log out now, the customer may be left without service. Are you sure you want to continue?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700"
              data-testid="logout-cancel-btn"
            >
              Stay Logged In
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmForceLogout}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="logout-confirm-btn"
            >
              Log Out Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContractorDashboard;
